import type { Huddle, PublicUser } from '@avslack/shared';
import { findChannelHuddle, useActiveHuddles } from '../data/huddles';
import { useDeferredValue, useState, ViewTransition } from 'react';
import { openHuddleWindow } from '../lib/huddle-window';
import { Avatar } from './Avatar';
import { Icon } from './Icon';
import { useUsers } from '../data/users';

/**
 * A csatorna huddle-gombja.
 *
 * Egy gomb, ket jelentessel: ha nincs hivas, elindit; ha van, csatlakozik.
 * A felhasznalonak nem kell tudnia, melyik eset all fenn - a szandeka
 * mindket esetben ugyanaz.
 */
export function HuddleHeaderButton({ channelId }: { channelId: string }) {
  const { data: huddles } = useActiveHuddles();
  const [blocked, setBlocked] = useState(false);

  const active = findChannelHuddle(huddles, channelId);
  const joined = active?.participants.filter((p) => p.state === 'joined').length ?? 0;

  return (
    <>
      <button
        type="button"
        onClick={() => {
          void openHuddleWindow(channelId).then((opened) => setBlocked(!opened));
        }}
        title={active ? 'Csatlakozás az öleléshez' : 'Ölelés indítása'}
        /*
          KIEMELT gomb, mindig - nem csak akkor, ha eppen folyik hivas.
          Az oleles a projekt fo indoka: ez a felulet legfontosabb cselekvese,
          es ugy is kell kineznie. A szint a valasztott szinsema akcentusa
          adja, tehat kovetni fogja, ha valaki mas temat allit be.
        */
        className={[
          'flex items-center gap-2 rounded-control px-2.5 py-2 text-sm font-semibold sm:px-3',
          'bg-accent text-accent-ink shadow-(--shadow-soft)',
          'transition-[background-color,box-shadow,scale] duration-(--duration-quick) hover:bg-accent-hover',
          'hover:shadow-(--shadow-lift) active:scale-95',
          // Folyamatban levo hivasnal lagy lüktetes hivja fel ra a figyelmet.
          active ? 'ring-2 ring-accent/35' : '',
        ].join(' ')}
      >
        <Icon name="huddle" size="lg" />
        {/*
          MOBILON csak az ikon.

          A felirat egy telefon fejlecen 60-70 keppontot vinne el - epp
          annyit, amennyi a CSATORNA NEVEHEZ kellene. Az ikon es a
          buboreksugo egyutt ugyanazt mondja el.
        */}
        <span className="hidden sm:inline">{active ? `Csatlakozás · ${joined}` : 'Ölelés'}</span>
      </button>

      {/*
        A felugro ablakot a bongeszo blokkolhatja. Nem hagyjuk nema
        kudarcban - megmondjuk, mit kell tenni.
      */}
      {blocked && (
        /*
          A fejlec gombsoraban all - SZUK hely: korlat es sortores nelkul
          osszenyomna a csatorna nevet.
        */
        <span role="alert" className="max-w-48 text-2xs leading-tight text-danger">
          A böngésző blokkolta az ablakot — engedélyezd a felugró ablakokat.
        </span>
      )}
    </>
  );
}

/**
 * Sav a csatorna tetejen, ha eppen folyik hivas, de nem vagyunk bent.
 *
 * A resztvevok NEVVEL latszanak - a "nem latszik, ki van bent" panasz
 * egyenes valasza. A lista a szerver adatbazisabol jon, nem becslesbol.
 */
export function HuddleBanner({ channelId, meId }: { channelId: string; meId: string }) {
  const { data: liveHuddles } = useActiveHuddles();
  const { nameOf } = useUsers();

  /*
    Ha valaki OLELEST INDIT, a sav felulrol becsuszik; a vegen kicsuszik. Ez
    pont az a "most tortent" jelzes, amit nem szabad elszalasztani.

    A halasztott ertek viszi az atmenetet. Csatornavaltaskor es az elso
    betolteskor az elo erteket mutatjuk - ott nem most indult a hivas.
  */
  const deferredChannelId = useDeferredValue(channelId);
  const deferredHuddles = useDeferredValue(liveHuddles);
  const huddles =
    deferredChannelId === channelId && deferredHuddles !== undefined
      ? deferredHuddles
      : liveHuddles;

  const active = findChannelHuddle(huddles, channelId);
  // Ha mar bent vagyunk, a sav nem szol nekunk.
  const amInside = active?.participants.some(
    (participant) => participant.user.id === meId && participant.state === 'joined',
  );
  if (!active || amInside) return null;

  return (
    <ViewTransition enter="slide-down-in" exit="slide-up-out" default="none">
      <div className="flex items-center gap-2.5 border-b border-border-soft bg-accent-soft px-4 py-2 sm:px-6">
        <span aria-hidden className="text-sm">
          <Icon name="huddle" />
        </span>

        <div className="flex min-w-0 items-center gap-2">
          <ParticipantStack huddle={active} />
          <p className="truncate text-xs text-ink">
            <span className="font-medium">{describeParticipants(active, nameOf)}</span>
            {active.title ? ` · ${active.title}` : ' beszélgetnek itt'}
          </p>
        </div>

        <button
          type="button"
          onClick={() => void openHuddleWindow(channelId)}
          className="ml-auto shrink-0 rounded-control bg-accent px-3 py-1 text-xs font-semibold text-accent-ink transition-colors duration-(--duration-quick) hover:bg-accent-hover"
        >
          Csatlakozás
        </button>
      </div>
    </ViewTransition>
  );
}

function ParticipantStack({ huddle }: { huddle: Huddle }) {
  const { nameOf } = useUsers();
  return (
    <div className="flex -space-x-1.5">
      {huddle.participants
        .filter((participant) => participant.state === 'joined')
        .slice(0, 4)
        .map((participant) => (
          <span
            key={participant.user.id}
            className="rounded-md ring-2 ring-[var(--accent-soft)]"
            title={nameOf(participant.user)}
          >
            <Avatar
              userId={participant.user.id}
              name={participant.user.displayName}
              avatarUrl={participant.user.avatarUrl}
              size="sm"
            />
          </span>
        ))}
    </div>
  );
}

function describeParticipants(huddle: Huddle, nameOf: (user: PublicUser) => string): string {
  const names = huddle.participants.map((p) => nameOf(p.user).split(' ')[0] ?? '');
  if (names.length === 0) return 'Senki';
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} és ${names[1]}`;
  return `${names[0]}, ${names[1]} és még ${names.length - 2}`;
}

/** Kis jelzes a sidebar csatorna-soran: folyik-e itt hivas. */
export function HuddleDot({ channelId }: { channelId: string }) {
  const { data: huddles } = useActiveHuddles();
  const active = findChannelHuddle(huddles, channelId);
  if (!active) return null;

  return (
    <span
      title={`${active.participants.length} fő ölelésben`}
      role="img"
      aria-label={`${active.participants.length} fő ölelésben`}
      className="flex shrink-0 items-center gap-0.5 rounded-pill bg-accent-soft px-1.5 py-0.5 text-2xs font-medium text-accent"
    >
      <span aria-hidden>
        <Icon name="huddle" />
      </span>
      {active.participants.length}
    </span>
  );
}
